import { create } from 'zustand';
import { CoinGeckoState, CoinMarketData } from '../types/coingecko';
import { fetchCoinMarkets, fetchCoinsByIds, fetchCategories } from '../services/coingeckoService';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const useCoingeckoStore = create<CoinGeckoState>((set, get) => ({
  coins: {},
  categories: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  categoriesLastUpdated: null,

  fetchMarketData: async () => {
    try {
      set({ isLoading: true, error: null });
      const data = await fetchCoinMarkets();
      const coinsMap: Record<string, CoinMarketData> = {};
      data.forEach(coin => {
        coinsMap[coin.id] = coin;
      });
      set({ coins: coinsMap, lastUpdated: Date.now(), isLoading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch market data', isLoading: false });
    }
  },

  fetchCategories: async () => {
    const state = get();
    const now = Date.now();
    const isCacheValid = state.categoriesLastUpdated && now - state.categoriesLastUpdated < CACHE_DURATION;

    // Return if cache is valid
    if (state.categories.length > 0 && isCacheValid) {
      return;
    }

    try {
      set({ isLoading: true, error: null });
      const categories = await fetchCategories();
      set({
        categories,
        categoriesLastUpdated: now,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch categories',
        isLoading: false,
      });
    }
  },

  getCoin: async (id: string) => {
    const state = get();
    const now = Date.now();
    const cachedCoin = state.coins[id];
    const isCacheValid = state.lastUpdated && now - state.lastUpdated < CACHE_DURATION;

    // Return cached data if valid
    if (cachedCoin && isCacheValid) {
      return cachedCoin;
    }

    try {
      const coins = await get().getCoins([id]);
      return coins[id];
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch coin data',
        isLoading: false,
      });
      return undefined;
    }
  },

  getCoins: async (ids: string[]) => {
    const state = get();
    const now = Date.now();
    const isCacheValid = state.lastUpdated && now - state.lastUpdated < CACHE_DURATION;

    // Filter out ids that are already cached and valid
    const idsToFetch = isCacheValid ? ids.filter(id => !state.coins[id]) : ids;

    if (idsToFetch.length === 0) {
      // Return cached data if all requested coins are cached and valid
      return ids.reduce(
        (acc, id) => {
          acc[id] = state.coins[id];
          return acc;
        },
        {} as Record<string, CoinMarketData>
      );
    }

    try {
      set({ isLoading: true, error: null });
      const newCoinsData = await fetchCoinsByIds(idsToFetch);

      // Merge new data with existing cache
      const updatedCoins = {
        ...state.coins,
        ...newCoinsData,
      };

      set({
        coins: updatedCoins,
        lastUpdated: now,
        isLoading: false,
      });

      // Return only the requested coins
      return ids.reduce(
        (acc, id) => {
          acc[id] = updatedCoins[id];
          return acc;
        },
        {} as Record<string, CoinMarketData>
      );
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch coins data',
        isLoading: false,
      });
      return {};
    }
  },
}));

export default useCoingeckoStore;
